import { Injectable } from '@angular/core';
import * as OktaAuth from '@okta/okta-auth-js';
import { OktaConstant } from '../constants/okta.constant';
import { OktaRoutes } from '../constants/route.constant';
import { Environment } from '../../shared/models/environment';

@Injectable({
  providedIn: 'root'
})
export class OktaPkceAuthService {
  readonly auth = { validate: true };
  private oktaAuth: OktaAuth = null;
  private redirectUrls = {
    login: window.location.origin,
    callback: window.location.origin + OktaRoutes.Callback.Url
  };
  private pkceConfig = {
    url: '',
    issuer: '',
    clientId: '',
    pkce: true,
    grantType: 'authorization_code',
    redirectUri: '',
    tokenManager: {
      autoRenew: false
    }
  };
  
  constructor() {}
  
  mapProperties = function(dest: any, src: any) {
    for (const property in src) {
      if (src.hasOwnProperty(property) && dest[property] !== undefined) {
        dest[property] = src[property];
      }
    }
  };

  init() {
    const config = Environment.getInstance().oktaConfig;
    this.mapProperties(this.pkceConfig, config);
    this.pkceConfig.url = config.issuer;
    this.oktaAuth = this.getOktaAuth();
  }

  isAuthCallback() {
    return window.location.href.startsWith(this.redirectUrls.callback);
  }

  async isAuthenticated() {
    // Checks if there is a current idToken in the TokenManger.
    return this.oktaAuth
      ? !!(await (this.oktaAuth.tokenManager.get(OktaConstant.IdToken) &&
          this.oktaAuth.tokenManager.get(OktaConstant.AccessToken)))
      : true;
  }

  async getToken(tokenName: string = OktaConstant.AccessToken) {
    return await this.oktaAuth.tokenManager.get(tokenName);
  }

  authenticate(userName: string, password: string): Promise<any> {
    const self = this;

    return this.oktaAuth
      .signIn({
        username: userName,
        password: password
      })
      .then(function(response) {
        if (response.status === 'SUCCESS') {
          self.oktaAuth.session.setCookieAndRedirect(
            response.sessionToken,
            self.pkceConfig.redirectUri
          );
        } else {
          console.error('We cannot handle the ' + response.status + ' status');
        }

        return Promise.resolve(response);
      })
      .fail(function(error) {
        return Promise.reject(error);
      });
  }

  initAuthCode() {
    return this.oktaAuth.token.getWithRedirect({
      responseType: this.pkceConfig.pkce ? 'code' : ['token', 'id_token'],
      prompt: 'none',
      scopes: ['openid', 'profile', 'email']
    });
  }

  async redirect() {
    return this.oktaAuth.token
      .parseFromUrl()
      .then(tokens => {
        const response = {
          idToken: null,
          accessToken: null,
          hasError: false
        };
        tokens.forEach(token => {
          if (token.idToken) {
            response.idToken = token.idToken;
            this.oktaAuth.tokenManager.add(OktaConstant.IdToken, token);
          }

          if (token.accessToken) {
            response.accessToken = token.accessToken;
            this.oktaAuth.tokenManager.add(OktaConstant.AccessToken, token);
          }
        });

        response.hasError = response.accessToken ? false : true;
        return Promise.resolve(response);
      })
      .catch(function(error) {
        error.hasError = true;
        return Promise.resolve(error);
      });
  }

  async signOut() { 
    const accessToken = await this.oktaAuth.tokenManager.get(OktaConstant.AccessToken);
    if (accessToken) {
      await this.oktaAuth.signOut({
        accessToken: accessToken,
        revokeAccessToken: true,
      });
    }
    
    await this.oktaAuth.tokenManager.clear();
  }

  getOktaAuth(redirectUrl: string = this.redirectUrls.callback) {
    if (this.oktaAuth) {
      return this.oktaAuth;
    }

    if (!OktaAuth.features.isPKCESupported()) {
      this.pkceConfig.pkce = false;
      this.pkceConfig.grantType = '';
    }

    this.pkceConfig.redirectUri = redirectUrl;
    return new OktaAuth(this.pkceConfig);
  }

}
